import { useState } from "react";
import { formatDate } from "../format.js";
import { useParams } from "react-router-dom";
import { Breadcrumb } from "../shell/Shell.js";
import { useSession } from "../auth/session.js";
import { Card, ConfidencePill, EmptyState, MetricTile, Panel } from "../components.js";
import { Button, Checkbox, Field, Select } from "../components/controls.js";
import {
  canSubmitAssessment,
  CAPABILITY_QUESTIONS,
  liveAssessmentService,
  type AssessmentService,
  type CapabilityAnswerInput,
  type CapabilityAssessmentView,
  type CapabilitySignalView,
  versionsOf,
} from "../api/assessmentService.js";
import "./assessment.css";

/**
 * E8.10 (NX-2638) — Assessment: the day-one path for a project with no connectors yet
 * (design/08 §2 flow 3). The API to accept a declared CapabilityAssessment has existed since
 * NX-2568; until now nothing could actually submit one.
 *
 * Two shape decisions worth naming:
 *
 * 1. The form is DRIVEN BY `CAPABILITY_QUESTIONS`, not written per dimension. There is no markup
 *    anywhere below that names a dimension, so adding one upstream adds a question here and
 *    nothing else (AC1).
 *
 * 2. The nav entry points at `/assessment`, which carries its own project picker, because a
 *    primary-nav item cannot link to a project-scoped path without already having a project.
 *    `/projects/:id/assessment` is registered as well, so the screen stays deep-linkable exactly as
 *    AC1 words it — both routes render this one component, the second simply arrives with the
 *    project preselected.
 *
 * The submit affordance is withheld from a role outside the submit set (AC2). That is a UI
 * courtesy, not the control: the server's `requireRole` is still what enforces it, and a masked
 * `responder` is rendered as the API's own absence rather than back-filled from the session (AC5).
 */

// --- AdaptiveQuestionForm --------------------------------------------------
export function AdaptiveQuestionForm({
  answers,
  onChange,
  onSubmit,
  submitting,
  error,
}: {
  answers: Record<string, boolean>;
  onChange: (key: string, value: boolean) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}): React.ReactNode {
  return (
    <form
      className="assessform"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {CAPABILITY_QUESTIONS.map((question) => (
        <Field className="qrow" variant="checkbox" label={question.prompt} key={question.key}>
          <Checkbox
            id={`q-${question.key}`}
            checked={answers[question.key] ?? false}
            onChange={(event) => onChange(question.key, event.target.checked)}
          />
        </Field>
      ))}
      <Button type="submit" disabled={submitting}>
        {submitting ? "Submitting…" : "Submit assessment"}
      </Button>
      {error !== null && <EmptyState message={`Couldn't submit the assessment: ${error}`} />}
    </form>
  );
}

// --- CurrentAssessmentSummary ----------------------------------------------
function SignalTile({ signal }: { signal: CapabilitySignalView }): React.ReactNode {
  return (
    <MetricTile
      label={signal.dimension}
      value={String(signal.value)}
      confidence={signal.confidence}
    />
  );
}

export function CurrentAssessmentSummary({
  assessment,
}: {
  assessment: CapabilityAssessmentView | null;
}): React.ReactNode {
  if (assessment === null) {
    return (
      <EmptyState message="No assessment has been submitted for this project yet. Declaring your delivery practices is the day-one path when no connector is wired up." />
    );
  }
  return (
    <Card>
      <div className="assesshead">
        <h2>Version {assessment.version}</h2>
        <span className="meta">submitted {formatDate(assessment.submittedAt)}</span>
      </div>
      {/* The API masks `responder` for roles outside the responder set. Absence is rendered as
          absence — never back-filled from the session, which would be inventing the answer. */}
      <div className="meta">
        Responder:{" "}
        {assessment.responder === undefined ? (
          <span className="na-inline">not available to your role</span>
        ) : (
          assessment.responder
        )}
      </div>
      <div className="grid cols-3">
        {(Array.isArray(assessment.signals) ? assessment.signals : []).map((signal) => (
          <SignalTile key={signal.id} signal={signal} />
        ))}
      </div>
    </Card>
  );
}

// --- AssessmentHistory -----------------------------------------------------
export function AssessmentHistory({
  versions,
}: {
  versions: readonly CapabilityAssessmentView[];
}): React.ReactNode {
  if (!Array.isArray(versions) || versions.length === 0) {
    return <EmptyState message="No earlier versions — this project has never been assessed." />;
  }
  return (
    <div className="assesshistory">
      {versions.map((version) => (
        <div className="histrow" key={`${version.projectId}-${version.version}`}>
          <span className="hv">v{version.version}</span>
          <span className="meta">{formatDate(version.submittedAt)}</span>
          <ConfidencePill
            value={
              version.signals.length === 0
                ? null
                : Math.min(...version.signals.map((s: CapabilitySignalView) => s.confidence))
            }
          />
        </div>
      ))}
    </div>
  );
}

// --- the screen ------------------------------------------------------------
export function Assessment({
  service = liveAssessmentService,
}: {
  service?: AssessmentService;
} = {}): React.ReactNode {
  const { id } = useParams();
  const [picked, setPicked] = useState<string>("");
  const projectId = id ?? picked;

  return (
    <>
      <Breadcrumb items={[{ label: "Portfolio", to: "/portfolio" }, { label: "Assessment" }]} />
      <div className="head">
        <h1>Assessment</h1>
      </div>
      <div className="meta">
        Declare how this team actually delivers. A declared practice is evidence of a different kind
        from an observed one, and is labelled as such wherever it is used.
      </div>
      {id === undefined && <ProjectPicker service={service} picked={picked} onPick={setPicked} />}
      {projectId === "" ? (
        <EmptyState message="Choose a project to assess." />
      ) : (
        <AssessmentBody service={service} projectId={projectId} />
      )}
    </>
  );
}

function ProjectPicker({
  service,
  picked,
  onPick,
}: {
  service: AssessmentService;
  picked: string;
  onPick: (id: string) => void;
}): React.ReactNode {
  const projects = service.useProjects();
  const options = projects.status === "ready" ? projects.envelope.data : [];
  return (
    <Panel title="Project">
      <Field label="Project" className="projpick">
        <Select value={picked} onChange={(event) => onPick(event.target.value)}>
          <option value="">Select a project…</option>
          {options.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </Select>
      </Field>
      {projects.status === "error" && (
        <EmptyState message={`Couldn't load the project list: ${projects.error.message}`} />
      )}
    </Panel>
  );
}

/**
 * Split out so the assessment hooks are only mounted once a project is chosen — `useApi` has no
 * skip mode, and a sentinel path would issue a real request and park the caller in `loading`.
 */
function AssessmentBody({
  service,
  projectId,
}: {
  service: AssessmentService;
  projectId: string;
}): React.ReactNode {
  const { session } = useSession();
  const maySubmit = session ? canSubmitAssessment(session.role) : false;
  const current = service.useCurrent(projectId);
  const history = service.useHistory(projectId);

  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<CapabilityAssessmentView | null>(null);

  const onSubmit = (): void => {
    setSubmitting(true);
    setError(null);
    const payload: CapabilityAnswerInput[] = CAPABILITY_QUESTIONS.map((question) => ({
      dimension: question.key,
      value: answers[question.key] ?? false,
    }));
    service
      .submit(projectId, payload, session?.token ?? null)
      .then((envelope) => setSubmitted(envelope.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setSubmitting(false));
  };

  // A just-submitted version is the newest truth on screen; otherwise show what the API returned.
  const currentView = submitted ?? (current.status === "ready" ? current.envelope.data : null);

  return (
    <>
      <Panel title="Current assessment">
        {current.status === "loading" && !submitted && (
          <div className="meta">Loading this project&rsquo;s assessment…</div>
        )}
        {current.status === "error" && !submitted && (
          <EmptyState message={`Couldn't load the assessment: ${current.error.message}`} />
        )}
        {(current.status === "ready" || submitted !== null) && (
          <CurrentAssessmentSummary assessment={currentView} />
        )}
      </Panel>

      {maySubmit ? (
        <Panel title="Declare practices">
          <AdaptiveQuestionForm
            answers={answers}
            onChange={(key, value) => setAnswers((prev) => ({ ...prev, [key]: value }))}
            onSubmit={onSubmit}
            submitting={submitting}
            error={error}
          />
        </Panel>
      ) : (
        <Panel title="Declare practices">
          <EmptyState message="You are signed in, but your role is not authorized to submit an assessment." />
        </Panel>
      )}

      <Panel title="Version history">
        {history.status === "loading" && <div className="meta">Loading earlier versions…</div>}
        {history.status === "error" && (
          <EmptyState message={`Couldn't load the version history: ${history.error.message}`} />
        )}
        {history.status === "ready" && (
          <AssessmentHistory versions={versionsOf(history.envelope.data)} />
        )}
      </Panel>
    </>
  );
}
